import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, ExternalLink, Search, Globe, MoreHorizontal } from 'lucide-react';
import FeaturedLayout from '../layouts/FeaturedLayout';
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  ImageUploader
} from '../ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { Textarea } from '../ui/textarea';
import { getAllLigas, createLiga, updateLiga, deleteLiga } from '../../lib/supabase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { MUNDO_INTERNO, MUNDO_RED } from '../../lib/mundoTorneo';
import { ligasDelMundo, seCreanLigasEnEsteMundo } from '../../lib/ligasPorMundo';

// Una misma pantalla, dos casas -- y cada casa ve SOLO lo suyo:
//
//   MUNDO_INTERNO  "Ligas": las que el club creo para nombrar la competencia de afuera
//                  de sus torneos internos. Se crean, se editan, se desactivan.
//
//   MUNDO_RED      "Ligas donde participo": las de las organizadoras de los torneos de
//                  MYKLUBB Torneos donde el club esta inscrito. El club NO las crea (ya
//                  existian) ni las toca. Solo las ve.
//
// Antes era una sola lista con las dos secciones y un boton de crear arriba, archivada
// en el bloque de la red: ofrecia crear justo donde no se crea, y faltaba donde si.
const TEXTOS = {
  [MUNDO_INTERNO]: {
    titulo: 'Ligas',
    subtitulo: 'Las competencias donde juega tu club y que no están en MYKLUBB. Sirven para nombrar tus torneos internos.',
    vacio: 'Tu club todavía no ha registrado ninguna liga.',
  },
  [MUNDO_RED]: {
    titulo: 'Ligas donde participo',
    subtitulo: 'Son de las organizadoras de los torneos de MYKLUBB Torneos en los que participa tu club. Se ven, no se editan.',
    vacio: 'Tu club todavía no participa en ninguna liga de MYKLUBB Torneos. Llegan solas al aceptar una invitación o inscribirte a un torneo.',
  },
};

// `mia` decide si se puede tocar: las de la red son de la organizadora del torneo y el
// club solo las ve -- la RLS tampoco lo dejaria editarlas, asi que ofrecer el boton
// seria prometer algo que la base rechaza.
const LigaCard = ({ liga, mia, onEdit, onDelete }) => (
  <Card className="hover:shadow-md transition-shadow">
    <CardContent className="pt-4 pb-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{liga.nombre}</p>
          {liga.organizacion && (
            <p className="text-xs text-muted-foreground truncate">{liga.organizacion}</p>
          )}
        </div>
        {liga.logo_url ? (
          <img src={liga.logo_url} alt={liga.nombre} className="h-10 w-10 rounded object-contain bg-muted border shrink-0" />
        ) : (
          <div className="h-10 w-10 rounded bg-muted border flex items-center justify-center shrink-0">
            <Globe className="h-5 w-5 text-muted-foreground" />
          </div>
        )}
      </div>

      {liga.descripcion && (
        <p className="text-xs text-muted-foreground line-clamp-2">{liga.descripcion}</p>
      )}

      {liga.sitio_web && (
        <a href={liga.sitio_web} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-xs text-primary hover:underline">
          <ExternalLink className="h-3 w-3 mr-1" /> Sitio web
        </a>
      )}

      {mia ? (
        <div className="flex items-center gap-2 pt-1.5 border-t">
          <Button size="sm" className="h-8 text-xs flex-1" onClick={onEdit}>
            <Edit2 className="h-3.5 w-3.5 mr-1.5" /> Editar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" title="Mas acciones">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
                <Trash2 className="h-4 w-4" />
                Desactivar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : (
        <div className="pt-1.5 border-t">
          <p className="text-xs text-muted-foreground">Liga de la organizadora del torneo.</p>
        </div>
      )}
    </CardContent>
  </Card>
);

const LigasManager = ({ mundo = MUNDO_INTERNO }) => {
  const { org } = useOrganization();
  const { confirm, alert, ConfirmDialog } = useConfirmDialog();
  const [ligas, setLigas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLiga, setEditingLiga] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '', descripcion: '', organizacion: '', logo_url: '', sitio_web: ''
  });
  const [saving, setSaving] = useState(false);
  const guard = useUnsavedGuard({ onClose: () => setShowModal(false) });

  const textos = TEXTOS[mundo] || TEXTOS[MUNDO_INTERNO];
  const puedeCrear = seCreanLigasEnEsteMundo(mundo);

  useEffect(() => { loadLigas(); }, []);

  const loadLigas = async () => {
    try {
      setLoading(true);
      const { data, error } = await getAllLigas();
      if (error) throw error;
      setLigas(data || []);
    } catch (err) {
      console.error('Error loading ligas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (liga = null) => {
    setEditingLiga(liga);
    setFormData(liga
      ? { nombre: liga.nombre || '', descripcion: liga.descripcion || '', organizacion: liga.organizacion || '', logo_url: liga.logo_url || '', sitio_web: liga.sitio_web || '' }
      : { nombre: '', descripcion: '', organizacion: '', logo_url: '', sitio_web: '' }
    );
    guard.resetDirty();
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) { await alert('Error', 'El nombre es obligatorio'); return; }
    try {
      setSaving(true);
      const { error } = editingLiga
        ? await updateLiga(editingLiga.id, formData)
        : await createLiga({ ...formData, activo: true });
      if (error) throw error;
      await loadLigas();
      guard.resetDirty();
      setShowModal(false);
    } catch (err) {
      await alert('Error', err.message || 'No se pudo guardar la liga');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (liga) => {
    if (!await confirm('Desactivar liga', `¿Desactivar "${liga.nombre}"?`, { destructive: true })) return;
    const { error } = await deleteLiga(liga.id);
    if (error) { await alert('Error', 'No se pudo desactivar la liga'); return; }
    await loadLigas();
  };

  // `getAllLigas` trae todo lo que la RLS deja ver (las mias y las de la red). Aqui se
  // queda solo la casa de esta pantalla.
  const filteredLigas = ligasDelMundo(ligas, mundo, org?.id).filter((liga) => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return liga.nombre?.toLowerCase().includes(t) || liga.organizacion?.toLowerCase().includes(t);
  });

  return (
    <FeaturedLayout title={textos.titulo} subtitle={textos.subtitulo} refreshAction={loadLigas}>
      {/* Toolbar */}
      <div className="mb-4 flex items-center gap-2 flex-nowrap">
        <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input placeholder="Buscar liga..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <span className="text-sm text-muted-foreground hidden sm:block">
          <span className="font-medium text-foreground">{filteredLigas.length}</span> ligas
        </span>
        {puedeCrear && (
          <Button onClick={() => handleOpenModal()} className="h-10" aria-label="Nueva liga" title="Nueva liga">
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Nueva Liga</span>
          </Button>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Cargando ligas...</p>
      ) : filteredLigas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Globe className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground mb-2">
              {searchTerm ? 'No se encontraron ligas' : textos.vacio}
            </p>
            {!searchTerm && puedeCrear && (
              <Button size="sm" className="mt-2" onClick={() => handleOpenModal()}>
                <Plus className="h-4 w-4 mr-1" /> Crear primera liga
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredLigas.map((liga) => (
            <LigaCard
              key={liga.id}
              liga={liga}
              mia={puedeCrear}
              onEdit={() => handleOpenModal(liga)}
              onDelete={() => handleDelete(liga)}
            />
          ))}
        </div>
      )}

      {/* Modal: solo existe donde se crea */}
      {puedeCrear && (
        <Dialog open={showModal} onOpenChange={guard.onOpenChange}>
          <DialogContent {...guard.dirtyProps} className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingLiga ? 'Editar Liga' : 'Nueva Liga'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} placeholder="Ej: Liga Premier" required />
              </div>
              <div className="space-y-2">
                <Label>Organización</Label>
                <Input value={formData.organizacion} onChange={(e) => setFormData({ ...formData, organizacion: e.target.value })} placeholder="Ej: FMF, Liga Local" />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea value={formData.descripcion} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} rows={2} placeholder="Descripción de la liga..." />
              </div>
              <ImageUploader value={formData.logo_url} onChange={(url) => { guard.markDirty(); setFormData({ ...formData, logo_url: url }); }} bucket="logos" folder="ligas" label="Logo de la Liga" />
              <div className="space-y-2">
                <Label>Sitio Web</Label>
                <Input type="url" value={formData.sitio_web} onChange={(e) => setFormData({ ...formData, sitio_web: e.target.value })} placeholder="https://..." />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => guard.onOpenChange(false)} disabled={saving}>Cancelar</Button>
                <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : editingLiga ? 'Actualizar' : 'Crear'}</Button>
              </DialogFooter>
            </form>
            {guard.DiscardDialog}
          </DialogContent>
        </Dialog>
      )}

      <ConfirmDialog />
    </FeaturedLayout>
  );
};

export default LigasManager;
